import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Trash2, Plus, ScanSearch, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { StatusBadge, fmtDate } from './StatusBadge';

export function SuppressionsTab({ onChanged }) {
  const { api } = useAuth();
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], total: 0 });
  const [newEmail, setNewEmail] = useState('');
  const [raw, setRaw] = useState('');
  const [ingesting, setIngesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/api/admin/email/suppressions', { params: { search, limit: 200 } });
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load suppressions');
    }
  }, [api, search]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const add = async () => {
    if (!newEmail.trim()) return;
    try {
      await api.post('/api/admin/email/suppressions', { email: newEmail.trim() });
      toast.success('Added to suppression list');
      setNewEmail('');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Allow sending to ${email} again?`)) return;
    try {
      await api.delete(`/api/admin/email/suppressions/${encodeURIComponent(email)}`);
      toast.success('Removed from suppression list');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };

  const ingest = async () => {
    if (!raw.trim()) return;
    setIngesting(true);
    try {
      const { data: d } = await api.post('/api/admin/email/bounces/ingest', { raw });
      toast[d.count ? 'success' : 'info'](d.count ? `Processed ${d.count} bounce(s): ${d.parsed.map((p) => `${p.email} (${p.kind})`).join(', ')}` : 'No bounce recipients detected');
      setRaw('');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ingest failed'); }
    setIngesting(false);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="email-suppressions-tab">
      <div className="lg:col-span-2 space-y-3">
        <div className="flex gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email"
            className="bg-[#000B18] border-white/10 text-white" data-testid="suppression-search-input" />
        </div>
        <p className="text-xs text-[#5C7599]" data-testid="suppression-total">{data.total} suppressed address(es)</p>
        <div className="rounded-xl border border-white/10 divide-y divide-white/5">
          {data.items.length === 0 && <p className="p-6 text-center text-sm text-[#5C7599]" data-testid="suppression-empty">No suppressed addresses.</p>}
          {data.items.map((s) => (
            <div key={s.email} className="flex items-center gap-3 p-3 hover:bg-white/5" data-testid={`suppression-row-${s.email}`}>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate">{s.email}</div>
                <div className="text-xs text-[#5C7599] truncate">{s.code} {s.diagnostic} · {s.source} · {fmtDate(s.created_at)}</div>
              </div>
              <StatusBadge value={s.reason} />
              <Button size="sm" variant="ghost" className="text-[#FF5252]" onClick={() => remove(s.email)} data-testid={`suppression-remove-${s.email}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader><CardTitle className="text-white text-base">Suppress manually</CardTitle></CardHeader>
          <CardContent className="flex gap-2">
            <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@domain.com"
              className="bg-[#000B18] border-white/10 text-white" data-testid="suppression-add-input" />
            <Button onClick={add} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#5CF0FF]" data-testid="suppression-add-btn"><Plus className="w-4 h-4" /></Button>
          </CardContent>
        </Card>
        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader><CardTitle className="text-white text-base">Paste a Mailer-Daemon message</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-[#5C7599]">Paste the full original message (Show original → copy). Hard bounces are suppressed immediately.</p>
            <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={6}
              className="bg-[#000B18] border-white/10 text-white font-mono text-xs" data-testid="bounce-ingest-textarea" />
            <Button onClick={ingest} disabled={ingesting} className="w-full bg-[#00E5FF] text-[#000B18] hover:bg-[#5CF0FF]" data-testid="bounce-ingest-btn">
              {ingesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ScanSearch className="w-4 h-4 mr-2" />}Process bounce
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
